import React from "react";
import { Lock as LockIcon } from "@mui/icons-material";
import { Box, Divider, Grid, InputAdornment, TextField, Tooltip } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { Currency } from "client/api-v2";
import { FixedFeeRows } from "./FixedFeeRows";
import { UnitPriceRows } from "./UnitPriceRows";
import type { CreateRateCardFormValues } from "./createRateCardForm";
import { validateDecimalString } from "./createRateCardForm";

export const maxFinalPriceRoundDownToNearest = 1000;

export function RateCardTermsFields() {
  const { control } = useFormContext<CreateRateCardFormValues>();

  return (
    <>
      <Grid size={{ xs: 12, sm: 7 }}>
        <Controller
          name="label"
          control={control}
          rules={{
            validate: (value) => value.trim().length <= 200 || "Label must be 200 characters or fewer",
          }}
          render={({ field: { ref, ...rest }, fieldState }) => (
            <TextField
              {...rest}
              inputRef={ref}
              fullWidth
              label="Label"
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 3 }}>
        <Box sx={{ position: "relative" }}>
          <Tooltip title="New rate cards are always priced in USD">
            <LockIcon
              fontSize="small"
              sx={{
                color: "text.secondary",
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                right: 12,
                zIndex: 1,
              }}
            />
          </Tooltip>
          <TextField fullWidth disabled label="Currency" value={Currency.USD} />
        </Box>
      </Grid>

      <UnitPriceRows />

      <Grid size={{ xs: 12, sm: 5 }}>
        <Controller
          name="discountExponent"
          control={control}
          rules={{
            validate: (value) =>
              validateDecimalString(value, {
                required: "Discount exponent is required",
                mustBeNumber: "Discount exponent must be a number",
                negative: "Discount exponent must be between 0 and 1",
                maxDecimals: 4,
                overPrecise: "Discount exponent can have at most 4 decimal places",
                max: { value: 1, message: "Discount exponent must be between 0 and 1" },
              }),
            deps: "discountThresholdUnits",
          }}
          render={({ field: { ref, ...rest }, fieldState }) => (
            <TextField
              {...rest}
              inputRef={ref}
              fullWidth
              label="Discount exponent (V)"
              slotProps={{ htmlInput: { inputMode: "decimal" } }}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 5 }}>
        <Controller
          name="discountThresholdUnits"
          control={control}
          rules={{
            validate: {
              wholeNumber: (value) =>
                validateDecimalString(value, {
                  required: "Discount threshold is required",
                  mustBeNumber: "Discount threshold must be a whole number of units, zero or more",
                  negative: "Discount threshold must be a whole number of units, zero or more",
                  maxDecimals: 0,
                  overPrecise: "Discount threshold must be a whole number of units, zero or more",
                  max: { value: 2_147_483_647, message: "Discount threshold cannot exceed 2,147,483,647" },
                }),
              requiredWithExponent: (value, formValues) =>
                Number(formValues.discountExponent) === 0 ||
                Number(value) > 0 ||
                "A non-zero discount exponent requires a non-zero discount threshold",
            },
          }}
          render={({ field: { ref, ...rest }, fieldState }) => (
            <TextField
              {...rest}
              inputRef={ref}
              fullWidth
              label="Discount threshold (units)"
              slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]*" } }}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 5 }}>
        <Controller
          name="overallDiscountPercent"
          control={control}
          rules={{
            validate: (value) =>
              validateDecimalString(value, {
                required: "Overall discount is required",
                mustBeNumber: "Overall discount must be a number",
                negative: "Overall discount must be between 0 and 100%",
                maxDecimals: 2,
                overPrecise: "Overall discount can have at most 2 decimal places",
                max: { value: 100, message: "Overall discount must be between 0 and 100%" },
              }),
          }}
          render={({ field: { ref, ...rest }, fieldState }) => (
            <TextField
              {...rest}
              inputRef={ref}
              fullWidth
              label="Overall discount"
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                htmlInput: { inputMode: "decimal" },
              }}
              error={fieldState.invalid}
              helperText={fieldState.error?.message ?? "Taken off the total before the round-down, so it is a minimum"}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 5 }}>
        <Controller
          name="finalPriceRoundDownToNearest"
          control={control}
          rules={{
            validate: (value) =>
              validateDecimalString(value, {
                mustBeNumber: "Final price round-down must be a number",
                negative: "Final price round-down cannot be negative",
                maxDecimals: 2,
                overPrecise: "Final price round-down can have at most 2 decimal places",
                max: {
                  value: maxFinalPriceRoundDownToNearest,
                  message: `Final price round-down cannot exceed ${maxFinalPriceRoundDownToNearest.toLocaleString(
                    "en-US"
                  )}`,
                },
              }),
          }}
          render={({ field: { ref, ...rest }, fieldState }) => (
            <TextField
              {...rest}
              inputRef={ref}
              fullWidth
              label="Final price round-down"
              slotProps={{
                input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                htmlInput: { inputMode: "decimal" },
              }}
              error={fieldState.invalid}
              helperText={fieldState.error?.message ?? "Leave blank for no rounding"}
            />
          )}
        />
      </Grid>

      <Grid size={12}>
        <Divider />
      </Grid>

      <FixedFeeRows />
    </>
  );
}
